import React, { useCallback, useMemo, useEffect, useRef, useState } from 'react'
import type { CSSProperties } from 'react'

import PresentationNavContextProvider from '../PresentationNav/PresentationNavContext/PresentationNavContextProvider'
import PresentationNavMobile from '../PresentationNav/PresentationNavMobile/PresentationNavMobile'

import type { PresentationBlock, PresentationOptionV2, PresentationPageV2, PresentationSectionV2 } from 'paintscout'

import blockComponents, {
  AboutEstimatorBlock,
  AwardsBlock,
  BannerBlock,
  ButtonBlock,
  CardsBlock,
  CustomBlock,
  DividerBlock,
  DocumentsBlock,
  GalleryBlock,
  ImageBlock,
  LogoBlock,
  MissionBlock,
  PicturesBlock,
  QuoteBlock,
  ServicesBlock,
  SliderImageBlock,
  SocialLinksBlock,
  SpacerBlock,
  TermsBlock,
  TestimonialsBlock,
  TextBlock,
  ThreeColumnBlock,
  TwoColumnBlock,
  WorkOrderBlock
} from '../blocks'

import AddBlockButton from '../util/AddBlockButton'
import SectionDividerMenu from '../util/SectionDividerMenu'
import {
  notifySectionInserted,
  requestAddPanelAccordionOpen
} from '../PresentationEditSidebar/AddPanel/addPanelAccordion'
import BlockMenu from '../util/BlockMenu'
import { ReviewMarksProvider, ReviewBanner, ReviewBlock, REVIEW_BLOCK_GAP, useIsReviewMarked } from '../ReviewMarks'
import {
  getBlockPadding,
  getBlockBackgroundStyles,
  getVisibleBlocks,
  isEditorChromeClick,
  GLOBAL_FOOTER_BLOCK_KEY,
  PRESENTATION_CANVAS_ID,
  loadPresentationFonts,
  getPresentationFontFamilies,
  applyPresentationDragResult,
  buildDroppedBlock,
  buildDroppedSection,
  readBlockDragPayload,
  readSectionDragPayload,
  pickNearestDropZone,
  readCanvasDropZoneBands,
  CanvasDropZoneRegistryContext,
  CanvasDropZone,
  PRESENTATION_BLOCK_DND_TYPE,
  isBlockDragDisabled,
  hasTermsBlock,
  isQuoteCartButton,
  // spread onto each block wrapper below rather than written as a literal `data-block-key`,
  // so the attribute and the selector `getAnchorBlockKey` queries with cannot drift apart
  BLOCK_KEY_ATTRIBUTE,
  SECTION_KEY_ATTRIBUTE
} from '../util'
import type { CanvasDropZoneEntry } from '../util'
import IntroToEditorDialog from '../dialogs/IntroToEditorDialog'
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd'
import type { DropResult, DraggableProvided } from '@hello-pangea/dnd'

import { Grid, Typography, useMediaQuery, makeStyles, ClickAwayListener } from '@paintscout/ui'
import { getPresentationContentStylesNext } from '@paintscout/ui/styles'
import { useClientOptions, useDialogs } from '@paintscout/ui-business'
import { GlobalFooter, MissingRendererPlaceholder, Placeholder } from '../components'

import InsertDriveFileOutlinedIcon from '@material-ui/icons/InsertDriveFileOutlined'

import Quote from '../../Quote/Quote'
import QuoteTerms from '../../Quote/QuoteTerms'

import type { PresentationBlockProps } from '../'

import {
  getObjectLabels,
  getPresentationLogo,
  getQuoteNextPresentation,
  isTermsEmpty,
  resolvePageForKey
} from '@paintscout/util/builder'

import classnames from 'classnames'
import type { QuoteView } from '../../Quote'
import { useQuote, usePresentation } from '../../context'
import { useFormikContext } from 'formik'
import { useLocation } from 'react-router-dom'
import qs from 'query-string'
import { track } from 'shared/util/analytics'

import { useFrame } from 'react-frame-component'

import type { MuiTheme as Theme, StyleClasses } from '@paintscout/ui'

const useStyles = makeStyles<Theme, any>(
  (theme) => ({
    root: {
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: '#ffffff',
      ...theme.typography.body1,
      ['@media print']: {
        display: 'none'
      }
    },
    printOnly: {
      opacity: 0,
      position: 'absolute',
      visibility: 'hidden',
      ['@media print']: {
        opacity: 1,
        position: 'static',
        visibility: 'visible'
      }
    },
    // starts the terms on their own printed page, below the estimate. The rule was referenced by
    // the print-only copy long before it existed, so that div emitted `class="undefined"` and the
    // break never happened (#22699).
    pageBreak: {
      ['@media print']: {
        breakAfter: 'page',
        pageBreakAfter: 'always'
      }
    },
    content: (props) => ({
      ...props.presentationContentStyles,
      overflowWrap: 'break-word'
    }),
    pageBorder: {
      display: 'flex',
      alignItems: 'center',
      textAlign: 'center',
      color: theme.palette.secondary.main,
      '&:before, &:after': {
        content: '""',
        flex: 1,
        borderBottom: `1px dashed ${theme.palette.secondary.main}`
      },
      '&:before': {
        margin: '0 20px 0 -20px'
      },
      '&:after': {
        margin: '0 -20px 0 20px'
      }
    },
    addBlockButton: {
      display: 'none'
    },
    showAddBlockButton: {
      display: 'flex'
    },
    sectionWrapper: {
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center',
      backgroundSize: 'contain',
      position: 'relative',
      borderCollapse: 'separate'
    },
    hideSectionOverflow: {
      overflow: 'hidden'
    },
    pointer: {
      cursor: 'pointer'
    },
    backgroundWrapper: {
      position: 'absolute',
      left: 0,
      top: 0,
      width: '100%',
      height: '100%'
    },
    widths: {
      marginLeft: 'auto',
      marginRight: 'auto',
      boxSizing: 'border-box',
      width: 1080,
      maxWidth: '100%',
      ['@media print']: {
        width: 'auto'
      }
    },
    widthsResponsive: {
      [theme.breakpoints.down('md')]: {
        width: 718
      },
      [theme.breakpoints.down('sm')]: {
        width: '96%'
      }
    },
    quoteSectionBorder: {
      border: `1px solid ${theme.palette.grey[800]}`,
      borderRadius: theme.borderRadius.md,
      margin: theme.spacing(2, 0),
      padding: theme.spacing(6, 6),
      [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(4, 2)
      }
    },
    /**
     * Hover affordances for one block: its outline and its add-block buttons. BlockMenu (duplicate /
     * up / down / delete) is deliberately NOT wired through this CSS `:hover` rule — its visibility is
     * driven by `hoveredBlockKey` React state instead, passed down as a prop (see `onMouseEnter` /
     * `onMouseLeave` on this div below), so a future rename here can't silently kill it the way
     * #22107 once did with an unscoped literal className.
     *
     * Named `block` to match the element that wears it — #22107 renamed the `className` from
     * `classes.section` to `classes.block` without renaming the rule, which left the div with an
     * undefined class and silently killed every hover affordance on the canvas.
     */
    block: {
      position: 'relative',
      transition: 'margin 0.2s ease',
      '&:hover': {
        '& $addBlockButton': {
          display: 'flex'
        },
        // a marked block has ReviewBlock's wrapper between this div and its BlockWrapper
        '& > $sectionWrapperHovered, & > [data-review-block] > $sectionWrapperHovered': {
          '&::before': {
            display: 'block'
          }
        }
      }
    },
    // Room for the review pill. It sits here, not on ReviewBlock, so BlockMenu and the add-block
    // buttons, which position against this div, stay level with the block.
    blockReviewMarked: {
      marginTop: REVIEW_BLOCK_GAP,
      marginBottom: REVIEW_BLOCK_GAP
    },
    sectionWrapperHovered: {
      '&::before': {
        zIndex: 1,
        display: 'none',
        content: '""',
        position: 'absolute',
        left: -theme.spacing(3) + 2,
        right: -theme.spacing(3) + 2,
        top: -2,
        bottom: -2,
        border: `2px solid ${theme.palette.presentation.sectionBorder.hover}`,
        pointerEvents: 'none'
      }
    },
    focusBorder: {
      zIndex: 2,
      '&::before': {
        content: '""',
        display: 'block',
        position: 'absolute',
        left: -theme.spacing(3) + 2,
        right: -theme.spacing(3) + 2,
        top: -2,
        bottom: -2,
        // no background fill — StickyBid painted this white, but PaintScout's editor
        // canvas is grey and the fill showed as white slabs in the side gutters
        border: `2px solid ${theme.palette.presentation.sectionBorder.focus}`,
        zIndex: -1
      }
    },
    newPagePadding: {
      paddingBottom: 0
    },
    dummyNav: {
      position: 'relative',
      '&:hover': {
        '& $dummyNavText': {
          opacity: 1
        }
      }
    },
    dummyNavText: {
      position: 'absolute',
      zIndex: 3,
      left: '50%',
      top: '50%',
      transform: 'translate(-50%, -50%)',
      pointerEvents: 'none',
      opacity: 0,
      transition: 'opacity 350ms ease'
    }
  }),
  { name: 'Presentation' }
)

export interface PresentationProps {
  classes?: StyleClasses<typeof useStyles>
  presentation?: PresentationOptionV2
  page?: string
  isPreview?: boolean
  isScreenshot?: boolean
  editFromDialog?: boolean
  isSettings?: boolean
  presentationTheme?: Theme
  loadAllFonts?: boolean

  getHeight?: (height: number) => void
  onPresentationClick?: (key: string) => void

  /**
   * Editing callbacks. All address nodes by **key**, not by index path — the pre-#21990 versions
   * took `(sectionIndex, pageIndex)` pairs, which silently target the wrong node when the caller's
   * indices are stale (anything reordered or deleted between render and click). Implementations
   * delegate to the tree helpers in @paintscout/util, which no-op on an unresolved key.
   */
  handleCreateSection?: (newSection: PresentationSectionV2, args: { pageKey: string; index?: number }) => void
  handleCreateBlock?: (newBlock: PresentationBlock, args: { sectionKey: string; index?: number }) => void
  handleEditSection?: (section: PresentationSectionV2) => void
  handleEditBlock?: (block: PresentationBlock, isDirty?: boolean) => void
  handleDeleteBlock?: (blockKey: string) => void
  /** Whole-tree replace, used by drag-and-drop where several nodes shift at once. */
  handlePagesChange?: (pages: PresentationPageV2[]) => void
  showHelp?: boolean
  view?: QuoteView
  hidePreviewFrame?: boolean
}

/**
 * The editor canvas. Block options render in the always-visible right inspector
 * (`PresentationInspector`, mounted separately in `PresentationEdit`) rather than here — this
 * component only owns `focusedBlock`/`focusedElement` selection state, which the inspector reads via
 * `usePresentation()`.
 */
function Presentation(props: PresentationProps) {
  return (
    <ReviewMarksProvider>
      <PresentationContents {...props} />
    </ReviewMarksProvider>
  )
}

function PresentationContents({
  editFromDialog,
  getHeight,
  handleCreateSection,
  handleCreateBlock,
  handleDeleteBlock,
  handlePagesChange,
  isScreenshot,
  isSettings = true,
  loadAllFonts,
  presentationTheme: presentationThemeProp,
  showHelp,
  view,
  ...props
}: PresentationProps) {
  const {
    isEditable,
    preview,
    focusedBlock,
    setFocusedBlock,
    setFocusedElement,
    presentationTheme: presentationThemeHook,
    addPages,
    catalogDrag,
    setCatalogDrag,
    onSidebarPageChange
  } = usePresentation()

  // drives BlockMenu's visibility directly, rather than a plain CSS `:hover` rule keyed to an
  // unscoped literal className shared by convention with BlockMenu.tsx — that convention is exactly
  // what #22107 broke once already (see the `block` rule below) with no compiler or test signal
  const [hoveredBlockKey, setHoveredBlockKey] = useState('')

  // Lets a block's own interactive content (e.g. SliderImageBlock's before/after divider)
  // suspend that block's page-reorder drag handle while the cursor is over it — otherwise
  // @hello-pangea/dnd's window-level mousedown sensor wins the gesture over the block's own
  // content, since it doesn't recognize custom elements like <img-comparison-slider> as
  // interactive (see ENG-1520 / #22956). The focused block suspends its handle too — see
  // isBlockDragDisabled in ../util/dragBlocks.
  const [dragSuspendedBlockKey, setDragSuspendedBlockKey] = useState('')

  const presentationTheme = presentationThemeProp ?? presentationThemeHook
  const { setFieldValue, submitForm } = useFormikContext<PresentationOptionV2>()
  const isReviewMarked = useIsReviewMarked()
  const { dismissAllDialogs, openDialog, stack: dialogStack } = useDialogs()
  const { options } = useClientOptions()
  const { quote, estimator, isCustomerView } = useQuote()
  const objectLabels = getObjectLabels({ options, quote, invoice: quote.is_invoice })
  const presentation =
    props.presentation ??
    getQuoteNextPresentation({
      options,
      quote,
      current: true,
      isWorkOrder: view === 'work-order',
      isProductOrderForm: view === 'product-order-form'
    })

  // assigning inside useMemo to an outer `let` is a no-op memo — the callback only runs when the
  // dep changes, so the assignment never reached the render that used it (#22535)
  const presentationContentStyles = useMemo(
    () => (presentationTheme ? getPresentationContentStylesNext(presentationTheme, { noMargins: ['li'] }) : {}),
    [presentationTheme]
  )
  const classes = useStyles({ ...props, presentationContentStyles })

  const { window: win, document: doc } = useFrame()

  useEffect(() => {
    // The google fonts this presentation's style actually uses. This is what the editor (isSettings)
    // and the customer view load, since the style picker can select any family in the full Google
    // catalog, not just the curated set. `loadAllFonts` adds the prefab + curated set on top — see
    // getPresentationFontFamilies for why that is opt-in.
    // Called even when the list is empty. loadPresentationFonts always adds FALLBACK_FONT_FAMILY,
    // so it still resolves and sets the `fontsLoaded` flag that PresentationImbedPreview's loading
    // screen waits on. Returning early here left that flag unset for any presentation whose style
    // names no fetchable family — no style at all (createPresentationNextOption writes none), or
    // only system faces like Roboto and Georgia — and the preview then sat on its loading screen
    // for the full 10s poll cap (#22535).
    const fontFamilies = getPresentationFontFamilies({ style: presentation.advanced?.style, loadAllFonts })
    loadPresentationFonts({ win, doc, fontFamilies })
  }, [loadAllFonts, presentation.advanced?.style, win, doc])

  useEffect(() => {
    // give invoice presentations a background of theme.palette.grey['100']
    if (!isSettings && quote.is_invoice) {
      doc.querySelector('body').style.background = '#f5f5f5'
    }

    return () => {
      doc.querySelector('body').style.removeProperty('background')
    }
  }, [isSettings, quote.is_invoice, doc])

  const location = useLocation()
  const { isNew } = qs.parse(location.search) as {
    isNew: string
  }

  useEffect(() => {
    if (isSettings && window.localStorage.getItem('isIntroToEditorDialogDismissed') !== 'true' && isNew) {
      openDialog(IntroToEditorDialog, {
        onCancel: dismissAllDialogs
      })
    }
  }, [isSettings, isNew, openDialog, dismissAllDialogs])

  const handleClickOn = (ev: React.MouseEvent<Element, MouseEvent>, blockKey: string) => {
    if (isSettings && focusedBlock !== blockKey) {
      // a fresh block selection always starts at block level — the inspector's drill-in (if any)
      // belonged to whichever block was previously focused
      setFocusedBlock(blockKey)
      setFocusedElement(null)
    }
  }

  const handleClickAway = (ev: React.MouseEvent<Document, MouseEvent>, blockKey: string) => {
    if (focusedBlock === blockKey) {
      ev.stopPropagation()
      submitForm()

      // A sidebar-tree click sets the selection itself (focuses the block row it scrolled to), and a
      // click inside the inspector is editing the very thing this would deselect — clearing here as
      // well would be a race (or would drop focus mid-edit). isEditorChromeClick covers both.
      if (!isEditorChromeClick(ev.target)) {
        setFocusedBlock('')
        setFocusedElement(null)
      }
    }
  }

  const handleDragEnd = (result: DropResult) => {
    // droppableIds are container keys (a section key for a block drag, a page key for a section
    // drag), so the whole translation lives in applyPresentationDragResult — see ../util/dragBlocks
    const updated = applyPresentationDragResult(presentation, result)

    if (updated) {
      handlePagesChange(updated.pages)
    }
  }

  /** Drop target for a block tile dragged from the Add panel — see ../util/canvasCatalogDrop. */
  const handleDropBlock = (dataTransfer: DataTransfer, args: { sectionKey: string; index?: number }) => {
    const payload = readBlockDragPayload(dataTransfer)
    if (!payload) {
      return
    }

    const newBlock = buildDroppedBlock({ options, presentation, payload })
    if (!newBlock) {
      return
    }

    handleCreateBlock(newBlock, args)
    setFocusedBlock(newBlock.key)
    setCatalogDrag(null)
    track('presentation_block_added', {
      blockType: newBlock.type,
      presentationKey: presentation.key,
      source: 'catalog'
    })
  }

  /** Drop target for a section/layout tile dragged from the Add panel — see ../util/canvasCatalogDrop. */
  const handleDropSection = (dataTransfer: DataTransfer, args: { pageKey: string; index?: number }) => {
    const payload = readSectionDragPayload(dataTransfer)
    if (!payload) {
      return
    }

    // catalogDrag.images is the photo list the dragged tile previewed with — reuse it rather than
    // calling getDefaultPresentationImages again, which shuffles (#22688)
    const newSection = buildDroppedSection({ options, payload, images: catalogDrag?.images })
    if (!newSection) {
      return
    }

    handleCreateSection(newSection, args)
    setCatalogDrag(null)
    // closes the layout flyout so it no longer covers the section that just landed (#24863)
    notifySectionInserted()
  }

  /**
   * Edge auto-scroll while dragging a catalog tile near the top/bottom of the viewport — the actual
   * editor canvas scrolls the whole window (see `scrollToKey` in PresentationEdit.tsx), not a nested
   * scroll container, so this scrolls `window` rather than the mock's `#canvas-scroll` div.
   */
  const edgeScrollTimer = useRef<ReturnType<typeof setInterval> | null>(null)
  const edgeScrollSpeed = useRef(0)

  const stopEdgeScroll = () => {
    if (edgeScrollTimer.current != null) {
      clearInterval(edgeScrollTimer.current)
      edgeScrollTimer.current = null
    }
  }

  useEffect(() => stopEdgeScroll, [])

  /**
   * The canvas hit-tests every zone rail itself, instead of each rail owning its own dragover — see
   * `pickNearestDropZone`. The rails publish their rects and drop handlers here, the canvas accepts a
   * release anywhere inside itself, and that release lands in the nearest rail that takes the dragged
   * kind. Before #22688 a release a few pixels off a rail was rejected by the browser, with no
   * feedback at all.
   */
  const dropZonesRef = useRef<Map<string, CanvasDropZoneEntry>>(new Map())
  const [activeZoneId, setActiveZoneId] = useState<string | null>(null)

  const registerDropZone = useCallback((id: string, entry: CanvasDropZoneEntry) => {
    dropZonesRef.current.set(id, entry)

    return () => {
      dropZonesRef.current.delete(id)
    }
  }, [])

  const dropZoneRegistry = useMemo(
    () => ({ register: registerDropZone, activeZoneId }),
    [registerDropZone, activeZoneId]
  )

  useEffect(() => {
    if (!catalogDrag) {
      stopEdgeScroll()
      setActiveZoneId(null)
    }
  }, [catalogDrag])

  const pickZoneFor = (clientY: number) => {
    if (!catalogDrag) {
      return null
    }

    return pickNearestDropZone({
      zones: readCanvasDropZoneBands(dropZonesRef.current),
      kind: catalogDrag.kind,
      clientY
    })
  }

  const handleCanvasDrop = (ev: React.DragEvent) => {
    stopEdgeScroll()

    if (!catalogDrag) {
      return
    }

    ev.preventDefault()
    // recomputed from live rects rather than read off activeZoneId — that state is a render-time
    // snapshot, and falling back to it could resurrect a rail the pointer has already left
    const zoneId = pickZoneFor(ev.clientY)
    const zone = zoneId ? dropZonesRef.current.get(zoneId) : undefined

    setActiveZoneId(null)

    if (!zone) {
      // no rail on this page accepts the dragged kind — clear the drag rather than leave rails up
      setCatalogDrag(null)
      return
    }

    // the zone's own handler clears catalogDrag once it has inserted
    zone.onDrop(ev.dataTransfer)
  }

  const handleCanvasDragOver = (ev: React.DragEvent) => {
    if (!catalogDrag) {
      return
    }

    // without this the browser rejects the drop outright and shows a `not-allowed` cursor
    ev.preventDefault()
    ev.dataTransfer.dropEffect = 'copy'

    const nextZoneId = pickZoneFor(ev.clientY)
    if (nextZoneId !== activeZoneId) {
      setActiveZoneId(nextZoneId)
    }

    const band = 96
    const y = ev.clientY
    const viewportHeight = window.innerHeight
    let direction = 0
    if (y < band) {
      direction = -(1 - Math.max(y, 0) / band)
    } else if (viewportHeight - y < band) {
      direction = 1 - Math.max(viewportHeight - y, 0) / band
    }

    if (!direction) {
      stopEdgeScroll()
      return
    }

    edgeScrollSpeed.current = 18 * direction
    if (edgeScrollTimer.current == null) {
      edgeScrollTimer.current = setInterval(() => {
        window.scrollBy(0, edgeScrollSpeed.current)
      }, 16)
    }
  }

  const isPreview = props.isPreview ?? (!!preview && preview !== 'none')
  const editable = !isPreview && isSettings
  const dragEnabled = editable && isEditable && !!handlePagesChange
  const isPrint = useMediaQuery('print')
  // get size from useMediaQuery
  let size: 'desktop' | 'tablet' | 'mobile' = 'desktop'
  // fixed values (StickyBid's effective defaults) rather than theme-relative
  // 'md'/'sm': the ambient theme differs between the app (md=958/lg=1214) and
  // customer-view (md=768/lg=1024), which made an iPad landscape (1024px)
  // resolve as 'desktop' and strip the text inset entirely
  const isTablet = useMediaQuery(1280)
  const isMobile = useMediaQuery(960)
  if (isTablet) size = 'tablet'
  if (isMobile) size = 'mobile'

  const presentationStyle = presentation.advanced.style
  const { palette } = presentationStyle || {}
  const presetColors = palette?.title ? Object.values(palette.colors) : undefined

  if (getHeight) {
    const presentationEl = doc.getElementById('presentation')
    if (presentationEl) {
      getHeight(presentationEl.offsetHeight)
    }
  }

  /**
   * Which page the canvas is showing. A page renders on its own — pages are never stacked (#21990).
   *
   * `props.page` is whatever the nav or the sidebar tree last selected, and that can be a key at any
   * level: a page (nav tab), a section (single-page nav, or a tree row), or a block (tree row). So
   * resolve it to its owning page rather than assuming the level, and fall back to the first page
   * when the key is stale or absent.
   */
  const activePage = useMemo<PresentationPageV2>(
    () => resolvePageForKey(presentation, props.page),
    [presentation, props.page]
  )

  // blockPrefix (the formik path handed to each block) is absolute from the presentation root, so
  // it needs the active page's position, not just the section's
  const activePageIndex = Math.max(
    0,
    (presentation.pages ?? []).findIndex((candidate) => candidate.key === activePage?.key)
  )

  // the print-only copy below is a second document, so it must skip the terms when the presentation
  // already carries a terms block — otherwise the printed output holds the terms twice (#22699).
  //
  // `isTermsEmpty` rather than a bare tag-strip of `terms.content`: terms with no words still have
  // to print when the quote needs a signature, because the signature card renders inside
  // `QuoteTerms` (ENG-1510). The nav's own "View Terms" guards stay on the text test — they decide
  // navigation, not what the document shows.
  const hasTerms = !isTermsEmpty({ quote, options }) && !hasTermsBlock(presentation)

  return (
    <CanvasDropZoneRegistryContext.Provider value={dropZoneRegistry}>
      {!isScreenshot && !editable && (
        <div className={classes.printOnly}>
          <Quote {...({ inPresentation: true, printOnly: true } as any)} />
          {hasTerms && (
            <>
              <div className={classes.pageBreak}></div>
              <QuoteTerms isCustomerView={isCustomerView} isPreview={isPreview} />
            </>
          )}
        </div>
      )}
      <div
        className={classes.root}
        id={PRESENTATION_CANVAS_ID}
        // guided tour anchor — see tours/presentationTourSteps.ts
        data-tour={'canvas'}
        {...(editable && {
          onDragOver: handleCanvasDragOver,
          onDragLeave: stopEdgeScroll,
          onDrop: handleCanvasDrop
        })}
      >
        {editable && (
          <PresentationNavContextProvider
            allowResponse={false}
            presentation={presentation}
            view={view}
            isPreview={isPreview}
            isDummy
            isCustomerView
          >
            <div className={classes.dummyNav}>
              <Typography variant={'overline'} className={classes.dummyNavText}>
                See Preview to Try Navigation
              </Typography>
              <PresentationNavMobile classes={{}} />
            </div>
          </PresentationNavContextProvider>
        )}
        {editable && <ReviewBanner />}
        {(() => {
          const sectionsContent = (activePage?.sections ?? []).map((section, sectionIndex) => {
            // A section with no `blocks` array at all (rather than an empty one) shouldn't crash
            // the whole presentation — fall back to treating it as empty. Warn so a section that
            // silently renders as "empty" (and re-saves clean, e.g. by opening it in the editor)
            // is diagnosable rather than a document quietly losing content with no trace.
            if (!section.blocks) {
              console.warn(`Presentation: section "${section.key}" has no blocks array — rendering it as empty`)
            }
            const sectionBlocks = section.blocks ?? []
            if (sectionBlocks.length === 0 && isSettings) {
              // empty section
              const emptySectionPlaceholder = (
                <div className={classnames(classes.padding, classes.newPagePadding)}>
                  <Placeholder
                    Icon={InsertDriveFileOutlinedIcon}
                    buttonText={`Add Block`}
                    onClick={handleOpenAddBlocksPanel}
                  />
                </div>
              )
              return (
                <div id={section.key} key={section.key} {...{ [SECTION_KEY_ATTRIBUTE]: section.key }}>
                  {editable && (
                    <>
                      <CanvasDropZone
                        accepts={'section'}
                        onDrop={(dt) => handleDropSection(dt, { pageKey: activePage.key, index: sectionIndex })}
                        data-testid={`canvas-drop-zone-section-${sectionIndex}`}
                      />
                      <SectionDividerMenu
                        index={sectionIndex}
                        pageKey={activePage.key}
                        tooltipTitle={'Add / Edit Section'}
                      />
                    </>
                  )}
                  {editable && (
                    <CanvasDropZone
                      accepts={'block'}
                      onDrop={(dt) => handleDropBlock(dt, { sectionKey: section.key, index: 0 })}
                      data-testid={`canvas-drop-zone-block-${section.key}-0`}
                    />
                  )}
                  {dragEnabled ? (
                    <Droppable droppableId={section.key} type={PRESENTATION_BLOCK_DND_TYPE}>
                      {(dropProvided) => (
                        <div ref={dropProvided.innerRef} {...dropProvided.droppableProps}>
                          {emptySectionPlaceholder}
                          {dropProvided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  ) : (
                    emptySectionPlaceholder
                  )}
                  {/* blocks are added only from the Add panel (#24863); this (+) only adds sections */}
                  {addPages && (
                    <AddBlockButton
                      key={`add-section-bottom-new-page`}
                      addPages={addPages}
                      objectLabels={objectLabels}
                      options={options}
                      onAddSection={(newSection: PresentationSectionV2) =>
                        handleCreateSection(newSection, { pageKey: activePage.key, index: sectionIndex })
                      }
                      presentation={presentation}
                    />
                  )}
                </div>
              )
            } else {
              // page with section(s)
              //
              // `blockIndex` is the block's position in the UNFILTERED `section.blocks`, which is
              // what every mutation below addresses — handleAddBlock/handleDropBlock's `insertAt`,
              // BlockMenu's duplicate, and the formik `blocks[k]` path. `visibleIndex` is its
              // position among the blocks this surface actually renders, and is only ever the right
              // answer for "first/last rendered" and for react-dnd. Do not conflate them (#22775).
              const visibleBlocks = getVisibleBlocks({
                blocks: sectionBlocks,
                blockComponents,
                quote,
                options,
                estimator,
                isPrint,
                isSettings,
                editable
              })

              const blocksContent = visibleBlocks.map(({ block, blockIndex }, visibleIndex) => {
                const isTopBlock = visibleIndex === 0
                const isBottomBlock = visibleIndex === visibleBlocks.length - 1

                const blockContent = (dragProvided?: DraggableProvided) => (
                  <section
                    key={block.key}
                    ref={dragProvided?.innerRef}
                    {...(dragProvided?.draggableProps ?? {})}
                    {...(dragProvided?.dragHandleProps ?? {})}
                  >
                    <ClickAwayListener
                      onClickAway={(ev) => handleClickAway(ev, block.key)}
                      {...(dialogStack.length && { mouseEvent: false, touchEvent: false })}
                    >
                      <div
                        className={classnames(classes.block, {
                          [classes.blockReviewMarked]: editable && isReviewMarked(block)
                        })}
                        data-testid={'presentation-block-hover-wrapper'}
                        onClick={(ev) => handleClickOn(ev, block.key)}
                        onMouseEnter={() => setHoveredBlockKey(block.key)}
                        onMouseLeave={() => setHoveredBlockKey('')}
                      >
                        {/* blocks are added only from the Add panel (#24863); this (+) only adds sections */}
                        {isSettings && isEditable && addPages && isTopBlock && (
                          <AddBlockButton
                            key={`add-block-top-block-${block.key}`}
                            objectLabels={objectLabels}
                            options={options}
                            focused={focusedBlock === block.key}
                            classes={{
                              root: classnames({
                                [classes.addBlockButton]: true,
                                [classes.showAddBlockButton]: focusedBlock === block.key
                              })
                            }}
                            addPages={addPages}
                            onAddSection={(newSection: PresentationSectionV2) =>
                              handleCreateSection(newSection, { pageKey: activePage.key, index: sectionIndex })
                            }
                            presentation={presentation}
                          />
                        )}
                        <ReviewBlock
                          enabled={editable}
                          block={block}
                          blockPrefix={`pages[${activePageIndex}].sections[${sectionIndex}].blocks[${blockIndex}]`}
                        >
                          {getBlock({
                            type: block.type,
                            props: {
                              editFromDialog: editFromDialog,
                              focusedBlock: focusedBlock,
                              index: blockIndex,
                              isEditable: editable && focusedBlock === block.key,
                              isInvoice: quote.is_invoice,
                              isPreview: isPreview,
                              isSettings: isSettings,
                              objectLabels: objectLabels,
                              sectionIndex: sectionIndex,
                              presentationContentStyles: presentationContentStyles,
                              presetColors: presetColors,
                              block: block,
                              blockPrefix: `pages[${activePageIndex}].sections[${sectionIndex}].blocks[${blockIndex}]`,
                              setFieldValue: setFieldValue,
                              setDragSuspended: (suspended: boolean) =>
                                setDragSuspendedBlockKey((prev) => {
                                  if (suspended) return block.key
                                  return prev === block.key ? '' : prev
                                }),
                              showHelp: showHelp,
                              size: size,
                              submitForm: submitForm,
                              // Editor canvas (isSettings) needs the live, currently-edited value so toggling
                              // "Use Quick Cart" shows/hides the button immediately, without a save (#23881). Everywhere
                              // else, keep the frozen quote.presentationTemplate snapshot so this matches
                              // PresentationNavMobile/PresentationNavFull's frozen-snapshot read — the cart-trigger
                              // button here and the drawer that listens for it must agree on whether the drawer
                              // exists (#23207).
                              // The block prop means "render the quote as the cart button", which is
                              // quick cart on a single-page presentation only (#24058) — so the
                              // QuoteBlock and BlockWrapper's isEmbeddedQuote styling agree.
                              useCart: isQuoteCartButton({
                                useCart: isSettings
                                  ? presentation.advanced?.useCart
                                  : quote.presentationTemplate?.advanced?.useCart,
                                presentation
                              }),
                              WrapperComponent: BlockWrapper
                            },
                            logo: getPresentationLogo({ options, quote, presentation }).logo
                          })}
                        </ReviewBlock>
                        {isSettings && isEditable && addPages && isBottomBlock && (
                          <AddBlockButton
                            key={`add-block-bottom-block-${block.key}`}
                            objectLabels={objectLabels}
                            options={options}
                            focused={focusedBlock === block.key}
                            classes={{
                              root: classnames({
                                [classes.addBlockButton]: true,
                                [classes.showAddBlockButton]: focusedBlock === block.key
                              })
                            }}
                            addPages={addPages}
                            onAddSection={(newSection: PresentationSectionV2) =>
                              handleCreateSection(newSection, { pageKey: activePage.key, index: sectionIndex + 1 })
                            }
                            presentation={presentation}
                          />
                        )}
                        {isSettings && !isPreview && (
                          <BlockMenu
                            addPages={addPages}
                            isHovered={hoveredBlockKey === block.key}
                            focusedBlock={focusedBlock}
                            handleCreateBlock={handleCreateBlock}
                            handleDeleteBlock={handleDeleteBlock}
                            handlePagesChange={handlePagesChange}
                            index={blockIndex}
                            sectionKey={section.key}
                            presentation={presentation}
                            block={block}
                            setFocusedBlock={setFocusedBlock}
                          />
                        )}
                      </div>
                    </ClickAwayListener>
                  </section>
                )

                // react-dnd needs its Draggable indices unique and contiguous from 0 within a
                // Droppable, so this one takes the rendered position, not the unfiltered one.
                // They are the same number whenever `dragEnabled` is true: dragEnabled implies
                // `editable`, and the editable canvas filters nothing (getVisibleBlocks keeps a
                // no-renderer block when `editable`, and only drops an empty one when
                // `!isSettings`). That is what keeps `destination.index` usable verbatim as
                // `movePresentationNextBlock`'s `toIndex` — see ../util/dragBlocks.
                const draggableOrStatic = dragEnabled ? (
                  <Draggable
                    draggableId={block.key}
                    index={visibleIndex}
                    key={block.key}
                    isDragDisabled={isBlockDragDisabled({
                      blockKey: block.key,
                      dragSuspendedBlockKey,
                      focusedBlock
                    })}
                  >
                    {(dragProvided) => blockContent(dragProvided)}
                  </Draggable>
                ) : (
                  blockContent()
                )

                if (!editable) {
                  return draggableOrStatic
                }

                // zone rails bracket every block so a catalog block tile can be dropped at any
                // seam (see ../util/CanvasDropZone) — collapsed to 0 height outside a catalog drag
                return (
                  <React.Fragment key={`block-zones-${block.key}`}>
                    {/* "insert above this block", so it names this block's own unfiltered index */}
                    {isTopBlock && (
                      <CanvasDropZone
                        accepts={'block'}
                        onDrop={(dt) => handleDropBlock(dt, { sectionKey: section.key, index: blockIndex })}
                        data-testid={`canvas-drop-zone-block-${section.key}-${blockIndex}`}
                      />
                    )}
                    {draggableOrStatic}
                    <CanvasDropZone
                      accepts={'block'}
                      onDrop={(dt) => handleDropBlock(dt, { sectionKey: section.key, index: blockIndex + 1 })}
                      data-testid={`canvas-drop-zone-block-${section.key}-${blockIndex + 1}`}
                    />
                  </React.Fragment>
                )
              })

              return (
                <div id={section.key} key={section.key} {...{ [SECTION_KEY_ATTRIBUTE]: section.key }}>
                  {editable && (
                    <>
                      <CanvasDropZone
                        accepts={'section'}
                        onDrop={(dt) => handleDropSection(dt, { pageKey: activePage.key, index: sectionIndex })}
                        data-testid={`canvas-drop-zone-section-${sectionIndex}`}
                      />
                      <SectionDividerMenu
                        index={sectionIndex}
                        pageKey={activePage.key}
                        tooltipTitle={'Add / Edit Section'}
                      />
                    </>
                  )}
                  {dragEnabled ? (
                    <Droppable droppableId={section.key} type={PRESENTATION_BLOCK_DND_TYPE}>
                      {(dropProvided) => (
                        <div ref={dropProvided.innerRef} {...dropProvided.droppableProps}>
                          {blocksContent}
                          {dropProvided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  ) : (
                    blocksContent
                  )}
                </div>
              )
            }
          })

          return dragEnabled ? (
            <DragDropContext onDragEnd={handleDragEnd}>{sectionsContent}</DragDropContext>
          ) : (
            sectionsContent
          )
        })()}
        {editable && (
          <CanvasDropZone
            accepts={'section'}
            onDrop={(dt) =>
              handleDropSection(dt, { pageKey: activePage?.key, index: (activePage?.sections ?? []).length })
            }
            data-testid={'canvas-drop-zone-section-end'}
          />
        )}
        {editable && <SectionDividerMenu index={-1} pageKey={activePage?.key} tooltipTitle={'Add Section'} />}
        <ClickAwayListener
          onClickAway={(ev) => handleClickAway(ev, GLOBAL_FOOTER_BLOCK_KEY)}
          {...(dialogStack.length && { mouseEvent: false, touchEvent: false })}
        >
          <div onClick={(ev) => handleClickOn(ev, GLOBAL_FOOTER_BLOCK_KEY)} data-testid={'global-footer-wrapper'}>
            <GlobalFooter
              footer={presentation.advanced?.footer}
              isEditable={editable}
              isSelected={focusedBlock === GLOBAL_FOOTER_BLOCK_KEY}
              objectLabels={objectLabels}
              presentationContentStyles={presentationContentStyles}
              presetColors={presetColors}
              setFieldValue={setFieldValue}
              submitForm={submitForm}
            />
          </div>
        </ClickAwayListener>
      </div>
    </CanvasDropZoneRegistryContext.Provider>
  )

  /**
   * The empty-section placeholder opens the Add panel on Blocks — the only place blocks are added
   * from (#24863). The accordion request is written before the panel mounts (see addPanelAccordion.ts).
   */
  function handleOpenAddBlocksPanel() {
    requestAddPanelAccordionOpen('blocks')
    onSidebarPageChange?.('add')
  }
}

function getBlock(args: { type: string; props: PresentationBlockProps; logo: string }) {
  const { logo, props, type } = args

  // Sherwin-Williams partner content (#21311) — dispatched via the section registry
  // rather than one case per type (Benjamin Moore is stale/unused and stays v1-only)
  if (type.startsWith('sherwin-williams')) {
    const PartnerSection = blockComponents?.[type]?.Section
    return PartnerSection ? <PartnerSection {...props} /> : getMissingRendererBlock({ type, props })
  }

  switch (type) {
    case 'logo':
      return <LogoBlock logo={logo} {...props} />
    case 'banner':
      return <BannerBlock {...props} />
    case 'button':
      return <ButtonBlock {...props} />
    case 'text':
      return <TextBlock {...props} />
    case 'image':
      return <ImageBlock {...props} />
    case '2-column':
      return <TwoColumnBlock {...props} />
    case '3-column':
      return <ThreeColumnBlock logo={logo} {...props} />
    case 'cards':
      return <CardsBlock {...props} />
    case 'gallery':
      return <GalleryBlock {...props} />
    case 'testimonials':
      return <TestimonialsBlock {...props} />
    case 'divider':
      return <DividerBlock {...props} />
    case 'social-links':
      return <SocialLinksBlock {...props} />
    case 'spacer':
      return <SpacerBlock {...props} />
    case 'quote':
      return <QuoteBlock {...props} />
    case 'terms':
      return <TermsBlock {...props} />
    case 'about-estimator':
      return <AboutEstimatorBlock {...props} />
    case 'awards':
      return <AwardsBlock {...props} />
    case 'documents':
      return <DocumentsBlock {...props} />
    case 'mission':
      return <MissionBlock {...props} />
    case 'services':
      return <ServicesBlock {...props} />
    case 'work-order':
      return <WorkOrderBlock {...props} />
    case 'custom':
      return <CustomBlock {...props} />
    case 'slider-image':
      return <SliderImageBlock {...props} />
    case 'pictures':
      return <PicturesBlock {...props} />
    default:
      // No v2 renderer for this type. Six catalog types land here — the v1-only `cover`,
      // `introduction`, `socials`, `sliderImage` and `benjamin-moore-main`, plus `video`, which is
      // in the catalog but was never built. Until #22775 this returned `undefined`, so the block
      // drew nothing and the author had no way to see or delete it. `getVisibleBlocks` only lets
      // one through on the editable canvas, and the placeholder is what makes it deletable.
      return getMissingRendererBlock({ type, props })
  }
}

/**
 * The placeholder for a block whose `type` has no v2 renderer. Forwards only the two flags the
 * placeholder gates on — spreading `props` would hand its `classes` (Presentation's own JSS keys)
 * to the placeholder's `makeStyles` as a class override.
 */
function getMissingRendererBlock(args: { type: string; props: PresentationBlockProps }) {
  const { type, props } = args

  return <MissingRendererPlaceholder type={type} isPreview={props.isPreview} isSettings={props.isSettings} />
}

export interface BlockWrapperProps extends PresentationBlockProps {
  children: React.ReactElement
}

const BlockWrapper = function ({
  children,
  index,
  block,
  isPreview,
  isInvoice,
  isSettings,
  sectionIndex,
  size,
  presentationContentStyles,
  focusedBlock,
  useCart,
  ...props
}: BlockWrapperProps) {
  const classes = useStyles({ ...props, presentationContentStyles })

  if (!block) {
    return null
  }

  const isQuoteSection = ['quote', 'work-order'].includes(block.type)
  const isEmbeddedQuote = isQuoteSection && !useCart
  const isInvoiceTerms = isInvoice && block.type === 'terms'
  const isPartnerSection = block.type.startsWith('sherwin-williams')

  const background = block?.background ?? {}
  const sectionStyles: CSSProperties = { ...getBlockBackgroundStyles({ block, isEmbeddedQuote, isInvoiceTerms }) }
  // Same poster-banner exclusion as getBlockBackgroundStyles.ts -- a stale `background.useTheme`
  // from before the block was switched to poster must not paint the themed background color either
  // (#23883).
  const isPosterBanner = block?.type === 'banner' && (block?.fields?.layout ?? 'poster') === 'poster'

  // Partner (Sherwin-Williams) sections are static, fully-designed marketing content that always
  // rendered edge-to-edge in v1 -- they have no Options component (createPartnerBlockModule wraps
  // only Section/isEmpty), so there's no "No Padding" checkbox a user could even reach to zero
  // this themselves. Falling through to the generic getBlockPadding default left every converted
  // partner section boxed in extra top/bottom whitespace neither v1 nor the static design called
  // for -- visible even between two adjacent partner sections, so it's not a banner-specific
  // margin issue (#22752/ENG-1439).
  sectionStyles.padding = isPartnerSection
    ? '0px 0px 0px 0px'
    : getBlockPadding({
        noTopPadding:
          sectionIndex === 0 &&
          index === 0 &&
          (block.type === 'banner' || block.type === 'image' || block.type === 'logo'),
        block,
        source: 'presentation',
        size,
        returnDefault: isEmbeddedQuote
      })

  return (
    <div
      id={block.key}
      {...{ [BLOCK_KEY_ATTRIBUTE]: block.key }}
      className={classnames({
        [classes.sectionWrapper]: true,
        [classes.hideSectionOverflow]: !isSettings,
        [classes.pointer]: isSettings && !isPreview && focusedBlock !== block.key,
        // Editor-only hover affordance (border shown on mouseover). Was unconditional, so any
        // consumer with real pointer events over this tree — the customer view, and this dialog's
        // preview once its pointer-events: none workaround was replaced with disableClicks — got
        // the editor's hover border despite not being in edit mode.
        [classes.sectionWrapperHovered]: isSettings && !isPreview,
        // the embedded quote renders the standard PaintScout quote UI — the presentation's
        // themed content styles (fonts/typography) must not bleed into it. When useCart is
        // on the quote block is just a themed cart button, which should stay styled.
        // Sherwin-Williams partner sections (#21311) are static SW-branded marketing content
        // reused from v1 — they must always keep their out-of-the-box SW styling regardless
        // of the presentation's selected style.
        [classes.content]: !isEmbeddedQuote && block.type !== 'work-order' && !isPartnerSection,
        [classes.focusBorder]: focusedBlock === block.key
      })}
    >
      <div style={sectionStyles} className={background.useTheme && !isPosterBanner ? 'themeBackground' : null}>
        <Grid
          container
          className={classnames({
            [classes.quoteSectionBorder]: false
          })}
          style={{ height: '100%' }}
        >
          <Grid item xs={12} style={{ height: '100%' }}>
            {children}
          </Grid>
        </Grid>
      </div>
    </div>
  )
}

export default Presentation
